import React, { useState, useMemo } from 'react'
import iconMap from '../../../settings/iconMap'
import {
  IWeatherDisplayProps,
  IWeatherDisplayState,
  IWeatherDisplayTemplateProps
} from '../../../types'
import WeatherDisplayDefault from './WeatherDisplayApricot'
import WeatherDisplayMoment from './WeatherDisplayMoment'

const fahrenheitSymbol = '°F'
const celsiusSymbol = '°C'

const app_theme = document.documentElement.getAttribute('data-mp-app-theme')

const WeatherDisplay = ({
  weatherData,
  locationData: location,
  source,
  iconPath,
  texts
}: IWeatherDisplayProps) => {
  const [state, setState] = useState<IWeatherDisplayState>({
    celsius: false,
    ariaLabel: '',
    ariaLiveVal: 'assertive'
  })
  const { celsius, ariaLabel, ariaLiveVal } = state

  const toggleTemp = (temp, switchTxt) => {
    const tmp = celsius ? temp : getTempInCelsius(temp)
    const activeUnit = celsius ? fahrenheitSymbol : celsiusSymbol
    const inActiveUnit = celsius ? celsiusSymbol : fahrenheitSymbol
    const ariaLabel = tmp + ' ' + activeUnit + '. ' + switchTxt + ' ' + inActiveUnit
    setState(() => ({ ...state, celsius: !celsius, ariaLabel: ariaLabel }))
  }
  const setAriaLabel = temp => {
    setState({ ...state, ariaLabel: temp })
  }
  const fixTemp = (temp, celsius) => {
    return celsius ? getTempInCelsius(temp) : Math.round(temp)
  }
  const getActiveUnit = celsius => {
    return celsius ? celsiusSymbol : fahrenheitSymbol
  }
  const getInActiveUnit = celsius => {
    return celsius ? fahrenheitSymbol : celsiusSymbol
  }
  const getActiveSpeed = () => {
    return celsius ? ' kp/h' : 'mph'
  }
  const fixSpeed = spd => {
    return celsius ? Math.round(spd * 1.60934) : spd
  }
  const getTempInCelsius = temp => {
    return Math.round((temp - 32) / 1.8)
  }

  const temperatureData = useMemo(() => {
    const temperatureFeelsLikeFixed = fixTemp(weatherData.temperatureFeelsLike, celsius)
    const temperatureMaxFixed = fixTemp(weatherData.temperatureMax, celsius)
    const temperatureMinFixed = fixTemp(weatherData.temperatureMin, celsius)
    const temperatureFixed = fixTemp(weatherData.temperature, celsius)

    return {
      temperatureFeelsLikeFixed,
      temperatureMaxFixed,
      temperatureMinFixed,
      temperatureFixed
    }
  }, [celsius, weatherData])

  const speedFixed = useMemo(() => {
    return fixSpeed(weatherData.windSpeed)
  }, [celsius, weatherData])

  const locationData = useMemo(() => {
    const cityName = location.city || ''
    const stateName = location.state || ''
    return { cityName, stateName }
  }, [location])

  const iconData = useMemo(() => {
    const iconData = iconMap[weatherData.iconCode]
    const iconLabel = iconData.description ?? ''
    const iconPath = iconData.iconUrl

    return { iconData, iconLabel, iconPath }
  }, [weatherData.iconCode])

  const labelData = useMemo(() => {
    const highTempLabel =
      texts.high_today + ' ' + temperatureData.temperatureMaxFixed + ' ' + texts.degrees
    const lowTempLabel =
      texts.low_today + ' ' + temperatureData.temperatureMinFixed + ' ' + texts.degrees
    const feelsLikeLabel =
      texts.feels + ' ' + temperatureData.temperatureFeelsLikeFixed + ' ' + texts.degrees
    const windSpeedLabel = texts.wind_speed + ' ' + speedFixed + getActiveSpeed()
    const humidityLabel = texts.humidity + ' ' + weatherData.relativeHumidity + '%'
    const degreesLabel = `${temperatureData.temperatureFixed} ${getActiveSpeed()}. ${
      texts.click_to_switch
    } ${getInActiveUnit(celsius)}`

    return {
      highTempLabel,
      lowTempLabel,
      feelsLikeLabel,
      windSpeedLabel,
      humidityLabel,
      degreesLabel
    }
  }, [celsius, temperatureData, speedFixed])

  const templateProps: IWeatherDisplayTemplateProps = {
    celsius,
    ariaLabel,
    ariaLiveVal,
    temperatureData,
    speedFixed,
    relativeHumidity: weatherData.relativeHumidity,
    dailyForecast: weatherData.daily,
    locationData,
    iconData,
    labelData,
    sourceData: source,
    texts,
    getActiveUnit,
    getInActiveUnit,
    setAriaLabel,
    toggleTemp,
    getActiveSpeed,
    fixTemp
  }

  const template =
    app_theme === 'moment' ? (
      <WeatherDisplayMoment {...templateProps} />
    ) : (
      <WeatherDisplayDefault {...templateProps} />
    )

  return template
}

export default WeatherDisplay
